import React, { useState, useEffect, useRef } from 'react';
import { X, Save, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS, API_BASE_URL } from '../../config/api';
import { AppointmentCreate, AppointmentType } from '../../types/appointment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { fetchCachedTeamMembers, type TeamMember } from '../../utils/teamMembersCache';

interface Practice {
  uuid: string;
  name: string;
}

interface PetOwner {
  uuid: string;
  full_name: string;
  email?: string;
  phone?: string;
}

interface Pet {
  id: string;
  name: string;
  species: string;
  breed?: string;
}

interface AppointmentCreateFormProps {
  onClose: () => void;
  onSuccess?: (appointment?: any) => void;
  initialDate?: Date;
  initialEndTime?: Date;
  initialVetUserId?: string;
  initialPetOwnerId?: string;
  initialPetId?: string;
}

const AppointmentCreateForm: React.FC<AppointmentCreateFormProps> = ({
  onClose,
  onSuccess,
  initialDate,
  initialEndTime,
  initialVetUserId,
  initialPetOwnerId,
  initialPetId
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Calculate duration from initial times if provided
  const initialDuration = initialDate && initialEndTime
    ? Math.round((initialEndTime.getTime() - initialDate.getTime()) / (1000 * 60))
    : 30;

  // Format initial date for input (YYYY-MM-DDTHH:MM format)
  const formatDateForInput = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const [formData, setFormData] = useState<AppointmentCreate>({
    practice_id: user?.practice_id || '',
    pet_owner_id: initialPetOwnerId || '',
    assigned_vet_user_id: initialVetUserId || user?.id || '',
    appointment_date: initialDate ? formatDateForInput(initialDate) : '',
    duration_minutes: initialDuration,
    appointment_type: AppointmentType.CHECKUP,
    title: '',
    description: '',
    notes: '',
    pet_ids: initialPetId ? [initialPetId] : []
  });

  const [practices, setPractices] = useState<Practice[]>([]);
  const [petOwners, setPetOwners] = useState<PetOwner[]>([]);
  const [pets, setPets] = useState<Pet[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [petOwnerSearch, setPetOwnerSearch] = useState('');
  const [showPetOwnerDropdown, setShowPetOwnerDropdown] = useState(false);
  const [filteredPetOwners, setFilteredPetOwners] = useState<PetOwner[]>([]);
  const petOwnerDropdownRef = useRef<HTMLDivElement>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    initialDate ? initialDate : null
  );

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (formData.pet_owner_id) {
      loadPetsForOwner(formData.pet_owner_id);
    } else {
      setPets([]);
      setFormData(prev => ({ ...prev, pet_ids: [] }));
    }
  }, [formData.pet_owner_id]);

  // Filter pet owners based on search
  useEffect(() => {
    if (petOwnerSearch.trim() === '') {
      setFilteredPetOwners(petOwners);
    } else {
      const searchLower = petOwnerSearch.toLowerCase();
      setFilteredPetOwners(
        petOwners.filter(owner =>
          owner.full_name.toLowerCase().includes(searchLower) ||
          owner.email?.toLowerCase().includes(searchLower) ||
          owner.phone?.includes(petOwnerSearch)
        )
      );
    }
  }, [petOwnerSearch, petOwners]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (petOwnerDropdownRef.current && !petOwnerDropdownRef.current.contains(event.target as Node)) {
        setShowPetOwnerDropdown(false);
      }
    };

    if (showPetOwnerDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPetOwnerDropdown]);

  const loadInitialData = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const baseURL = API_BASE_URL;

      const [practicesRes, petOwnersRes] = await Promise.all([
        fetch(API_ENDPOINTS.PRACTICES.LIST),
        fetch(API_ENDPOINTS.PET_OWNERS.LIST)
      ]);

      if (practicesRes.ok) {
        const practicesData = await practicesRes.json();
        setPractices(practicesData);
      }

      if (petOwnersRes.ok) {
        const petOwnersData = await petOwnersRes.json();
        setPetOwners(petOwnersData);
        
        // If we have an initial pet owner, set the search field to show their name
        if (initialPetOwnerId) {
          const selectedOwner = petOwnersData.find((owner: PetOwner) => owner.uuid === initialPetOwnerId);
          if (selectedOwner) {
            setPetOwnerSearch(selectedOwner.full_name);
          }
        }
      }

      // Load team members (with caching)
      if (user?.practice_id) {
        const token = localStorage.getItem('access_token');
        const members = await fetchCachedTeamMembers(user.practice_id, token);
        setTeamMembers(members);
      }
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Failed to load form data');
    }
  };

  const loadPetsForOwner = async (ownerId: string) => {
    try {
      const response = await fetch(API_ENDPOINTS.PETS.BY_OWNER(ownerId));
      if (response.ok) {
        const petsData = await response.json();
        setPets(petsData);
      } else {
        setPets([]);
      }
    } catch (err) {
      console.error('Error loading pets:', err);
      setPets([]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'duration_minutes' ? parseInt(value) || 0 : value
    }));
  };

  const handlePetSelection = (petId: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      pet_ids: checked
        ? [...prev.pet_ids, petId]
        : prev.pet_ids.filter(id => id !== petId)
    }));
  };

  // Generate dynamic title based on pet names, appointment type, and date/time
  const generateTitle = () => {
    const selectedPets = pets.filter(p => formData.pet_ids.includes(p.id));
    const petNames = selectedPets.map(p => p.name).join(', ') || 'Pet';

    const appointmentTypeLabel = formData.appointment_type
      ? formData.appointment_type.charAt(0).toUpperCase() + formData.appointment_type.slice(1)
      : 'Appointment';

    let dateTimeStr = '';
    if (formData.appointment_date) {
      const date = new Date(formData.appointment_date);
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      dateTimeStr = ` - ${dateStr} at ${timeStr}`;
    }

    return `${petNames} ${appointmentTypeLabel}${dateTimeStr}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Generate dynamic title and convert appointment_date to ISO format
      const appointmentData = {
        ...formData,
        title: generateTitle(),
        appointment_date: new Date(formData.appointment_date).toISOString()
      };

      const response = await fetch(API_ENDPOINTS.APPOINTMENTS.CREATE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(appointmentData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create appointment');
      }

      const createdAppointment = await response.json();
      onSuccess?.(createdAppointment);
      onClose();
    } catch (err) {
      console.error('Error creating appointment:', err);
      setError(err instanceof Error ? err.message : 'Failed to create appointment');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTimeForInput = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  return (
    <div className="flex flex-col h-full">
      {/* Custom DatePicker Styling */}
      <style>{`
        .react-datepicker {
          font-size: 1rem !important;
          border: 1px solid #d1d5db !important;
          border-radius: 0.5rem !important;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1) !important;
        }
        .react-datepicker__header {
          background-color: #f9fafb !important;
          border-bottom: 1px solid #e5e7eb !important;
          padding-top: 1rem !important;
        }
        .react-datepicker__current-month {
          font-size: 1.125rem !important;
          font-weight: 600 !important;
          color: #111827 !important;
          margin-bottom: 0.5rem !important;
        }
        .react-datepicker__day-name {
          width: 2.5rem !important;
          line-height: 2.5rem !important;
          margin: 0.25rem !important;
          font-size: 0.875rem !important;
          font-weight: 600 !important;
          color: #6b7280 !important;
        }
        .react-datepicker__day {
          width: 2.5rem !important;
          line-height: 2.5rem !important;
          margin: 0.25rem !important;
          font-size: 0.875rem !important;
          border-radius: 0.375rem !important;
        }
        .react-datepicker__day:hover {
          background-color: #dbeafe !important;
          border-radius: 0.375rem !important;
        }
        .react-datepicker__day--selected {
          background-color: #3b82f6 !important;
          color: white !important;
          font-weight: 600 !important;
        }
        .react-datepicker__day--keyboard-selected {
          background-color: #93c5fd !important;
        }
        .react-datepicker__time-container {
          border-left: 1px solid #e5e7eb !important;
        }
        .react-datepicker__time-list-item {
          font-size: 0.875rem !important;
          padding: 0.5rem !important;
        }
        .react-datepicker__time-list-item:hover {
          background-color: #dbeafe !important;
        }
        .react-datepicker__time-list-item--selected {
          background-color: #3b82f6 !important;
          color: white !important;
          font-weight: 600 !important;
        }
        .react-datepicker__navigation {
          top: 1rem !important;
        }
      `}</style>

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <h2 className="text-base font-semibold text-gray-900">Schedule New Appointment</h2>
        <button
          onClick={onClose}
          className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mx-4 mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Form Content */}
      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Practice Selection - Hidden, uses current user's practice */}
        <div className="hidden">
          <input
            type="hidden"
            name="practice_id"
            value={formData.practice_id}
          />
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Practice *
          </label>
          <div className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-600">
            {practices.find(p => p.uuid === formData.practice_id)?.name || 'Current Practice'}
          </div>
        </div>

        {/* Pet Owner Search */}
        <div className="relative" ref={petOwnerDropdownRef}>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Pet Owner *
          </label>
          <input
            type="text"
            value={petOwnerSearch}
            onChange={(e) => {
              setPetOwnerSearch(e.target.value);
              setShowPetOwnerDropdown(true);
            }}
            onFocus={() => setShowPetOwnerDropdown(true)}
            placeholder="Search by name, email, or phone..."
            required={!formData.pet_owner_id}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />

          {/* Dropdown */}
          {showPetOwnerDropdown && filteredPetOwners.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {filteredPetOwners.map(owner => (
                <button
                  key={owner.uuid}
                  type="button"
                  onClick={() => {
                    setFormData(prev => ({ ...prev, pet_owner_id: owner.uuid }));
                    setPetOwnerSearch(owner.full_name);
                    setShowPetOwnerDropdown(false);
                  }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 focus:bg-blue-50 focus:outline-none border-b border-gray-100 last:border-0"
                >
                  <div className="font-medium text-gray-900">{owner.full_name}</div>
                  {(owner.email || owner.phone) && (
                    <div className="text-xs text-gray-500 mt-0.5">
                      {owner.email && <span>{owner.email}</span>}
                      {owner.email && owner.phone && <span className="mx-1">•</span>}
                      {owner.phone && <span>{owner.phone}</span>}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}

        </div>

        {/* Pet Selection */}
        {pets.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Pets *
            </label>
            <div className="space-y-2">
              {pets.map(pet => (
                <label key={pet.id} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.pet_ids.includes(pet.id)}
                    onChange={(e) => handlePetSelection(pet.id, e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-1 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    {pet.name} ({pet.species}{pet.breed ? `, ${pet.breed}` : ''})
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Date & Time */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date & Time *
            </label>
            <DatePicker
              selected={selectedDate}
              onChange={(date: Date | null) => {
                setSelectedDate(date);
                if (date) {
                  setFormData(prev => ({
                    ...prev,
                    appointment_date: formatDateForInput(date)
                  }));
                }
              }}
              showTimeSelect
              timeIntervals={15}
              dateFormat="MM/dd/yyyy, hh:mm aa"
              required
              className="w-full px-3 py-2 text-base text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white cursor-pointer hover:border-gray-400 transition-colors"
              calendarClassName="text-base"
              wrapperClassName="w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Duration (minutes) *
            </label>
            <select
              name="duration_minutes"
              value={formData.duration_minutes}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white appearance-none cursor-pointer hover:border-gray-400 transition-colors"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                backgroundPosition: 'right 0.5rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1.5em 1.5em',
                paddingRight: '2.5rem'
              }}
            >
              <option value="15">15 minutes</option>
              <option value="30">30 minutes</option>
              <option value="45">45 minutes</option>
              <option value="60">1 hour</option>
              <option value="90">1 hour 30 minutes</option>
              <option value="120">2 hours</option>
              <option value="150">2 hours 30 minutes</option>
              <option value="180">3 hours</option>
              <option value="210">3 hours 30 minutes</option>
              <option value="240">4 hours</option>
              <option value="270">4 hours 30 minutes</option>
              <option value="300">5 hours</option>
              <option value="360">6 hours</option>
              <option value="480">8 hours</option>
              {/* If dragged duration doesn't match predefined options, show it */}
              {formData.duration_minutes && formData.duration_minutes > 0 &&
                ![15, 30, 45, 60, 90, 120, 150, 180, 210, 240, 270, 300, 360, 480].includes(formData.duration_minutes) && (
                  <option value={formData.duration_minutes}>
                    {Math.floor(formData.duration_minutes / 60) > 0
                      ? `${Math.floor(formData.duration_minutes / 60)} hour${Math.floor(formData.duration_minutes / 60) > 1 ? 's' : ''} ${formData.duration_minutes % 60 > 0 ? `${formData.duration_minutes % 60} min` : ''}`.trim()
                      : `${formData.duration_minutes} minutes`}
                  </option>
                )}
            </select>
          </div>
        </div>

        {/* Appointment Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Appointment Type *
          </label>
          <select
            name="appointment_type"
            value={formData.appointment_type}
            onChange={handleInputChange}
            required
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white appearance-none cursor-pointer hover:border-gray-400 transition-colors"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundPosition: 'right 0.5rem center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '1.5em 1.5em',
              paddingRight: '2.5rem'
            }}
          >
            <option value={AppointmentType.CHECKUP}>Checkup</option>
            <option value={AppointmentType.EMERGENCY}>Emergency</option>
            <option value={AppointmentType.SURGERY}>Surgery</option>
            <option value={AppointmentType.CONSULTATION}>Consultation</option>
          </select>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows={2}
            placeholder="Appointment details..."
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Internal Notes
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            rows={2}
            placeholder="Private notes (not visible to pet owner)..."
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </form>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={loading}
          className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={loading || !formData.practice_id || !formData.pet_owner_id || formData.pet_ids.length === 0}
          className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
        >
          <Save className="w-3.5 h-3.5" />
          {loading ? 'Creating...' : 'Create Appointment'}
        </button>
      </div>
    </div>
  );
};

export default AppointmentCreateForm;

